import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import './style.scss';

interface IProps {
  showScrollbar?: boolean;
  scrollbarSize?: 'small' | 'normal';
  direction?: 'horizontal' | 'vertical';
  className?: string;
  scrollClassName?: string;
  children?: ReactNode;
}

const ScrollableBlock: React.FC<IProps> = ({
  showScrollbar = false,
  scrollbarSize = 'normal',
  direction = 'vertical',
  ...props
}: IProps): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (direction === 'horizontal' && wrapperRef?.current) {
      wrapperRef.current.addEventListener('wheel', onWindowHorizontalScroll);
    }

    return () => {
      if (direction === 'horizontal' && wrapperRef?.current) {
        wrapperRef.current.removeEventListener('wheel', onWindowHorizontalScroll);
      }
    };
  }, []);

  const onWindowHorizontalScroll = (event: WheelEvent) => {
    event.preventDefault();
    if (!wrapperRef?.current) return;

    wrapperRef.current.scrollTo({ left: wrapperRef.current.scrollLeft + event.deltaY });
  };

  const className = useMemo((): string => {
    let _className: string = 'scrollable-block';

    if (direction === 'horizontal') _className += ' scrollable-block_horizontal';

    if (!showScrollbar) {
      _className += ' hide-scrollbar';
    } else if (scrollbarSize === 'small') {
      _className += ' scrollable-block__scroll_small';
    } else if (scrollbarSize === 'normal') {
      _className += ' scrollable-block__scroll_normal';
    }

    if (props.className) _className += ` ${props.className}`;

    return _className;
  }, [direction, showScrollbar, scrollbarSize, props.className]);

  const scrollClassName = useMemo((): string => {
    let _className: string = 'scrollable-block__scroll';

    if (props.scrollClassName) _className += ` ${props.scrollClassName}`;
    if (direction === 'horizontal') _className += ' scrollable-block__scroll_horizontal';

    return _className;
  }, [direction, props.scrollClassName]);

  return (
    <div ref={wrapperRef} className={className}>
      <div ref={scrollRef} className={scrollClassName}>
        {props.children}
      </div>
    </div>
  );
};

export default ScrollableBlock;
